import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  DataTable,
  DataTableColumnHeader,
  type DataTableToolbarProps,
  DateUI,
  FacetedFilterStandalone,
  LabelPill,
  Number as NumberUI,
  PrimaryFullScreenSpinner,
  ResetButton,
  toast,
} from "@repo/ui";
import {
  formatLocalDateComponents,
  NotFoundError,
  neverFunc,
} from "@repo/utils";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactNode, Suspense } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { BusinessEntityColumnCell } from "~/components/tables/BusinessEntityColumnCell";
import { graphql } from "~/gql/client";
import { assetBalanceValidationStatusValues } from "~/gql/client/enumValues";
import type { AdminAssetBalanceValidationRowFragment } from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute(
  "/__auth/asset-balance-validations_/$fileImportId",
)({
  head: () => opsHead("Balance Validation"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

graphql(`
  fragment AdminAssetBalanceValidationRow on AssetBalanceValidation {
    id
    status
    validatedAt
    effectiveAt
    ledgerUnitBalance
    fileUnitBalance
    unitBalanceDifference
    nonAssetUnits
    asset {
      id
      name
      friendlyIdentifier
      businessEntity {
        id
        name
        organisation {
          id
          name
        }
      }
      ... on FixedIncomeFundAsset {
        product {
          id
          name
        }
      }
    }
  }

  fragment AdminAssetBalanceValidationFileDetails on AssetBalanceValidationImportFile {
    id
    fileName
    uploadedAt
    effectiveAt
    status
    downloadUrl
    assetBalanceValidations {
      ...AdminAssetBalanceValidationRow
    }
  }
`);

function View() {
  const params = useParams({ strict: false });
  if (!params.fileImportId) {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminAssetBalanceValidationFile($id: ID!) {
        node(id: $id) {
          id
          ... on AssetBalanceValidationImportFile {
            ...AdminAssetBalanceValidationFileDetails
          }
        }
      }
    `),
    { variables: { id: params.fileImportId } },
  );

  const [validateFile, { loading: validating }] = useMutation(
    graphql(`
      mutation RevalidateAssetBalanceValidationFile($input: ValidateAssetBalanceFileInput!) {
        validateAssetBalanceFile(input: $input) {
          importFile {
            ...AdminAssetBalanceValidationFileDetails
          }
        }
      }
    `),
  );

  const file = data.node;
  if (!file || file.__typename !== "AssetBalanceValidationImportFile") {
    throw new NotFoundError();
  }

  useOpsTitle(`Balance validation "${file.fileName}"`);

  const validations = [...(file.assetBalanceValidations ?? [])].sort((a, b) => {
    const byDifference = b.unitBalanceDifference
      .abs()
      .comparedTo(a.unitBalanceDifference.abs());
    if (byDifference !== 0 || a.status === b.status) {
      return byDifference;
    }
    return a.status === "MISMATCHED" ? -1 : 1;
  });
  const mismatchedCount = validations.filter(
    (v) => v.status === "MISMATCHED",
  ).length;

  let statusPill: ReactNode;
  switch (file.status) {
    case "MATCHED":
      statusPill = <LabelPill variant="success" label="Matched" />;
      break;
    case "MISMATCHED":
      statusPill = <LabelPill variant="error" label="Mismatched" />;
      break;
    case null:
    case undefined:
      statusPill = <LabelPill variant="neutral" label="Not validated" />;
      break;
    default:
      neverFunc(file.status);
  }

  return (
    <AdminPageContainer fullWidth testId="admin-asset-balance-validation">
      <AdminPageHeader
        title={file.fileName}
        subtitle={
          <span className="flex items-center gap-2">
            {statusPill}
            {`${validations.length - mismatchedCount} matched, ${mismatchedCount} mismatched · ledger effective at ${file.effectiveAt ? formatLocalDateComponents(file.effectiveAt).datetime : "-"} · uploaded ${formatLocalDateComponents(file.uploadedAt).datetime}`}
          </span>
        }
        buttons={
          <div className="flex gap-2">
            {file.downloadUrl && (
              <Button variant="outline" asChild>
                <a href={file.downloadUrl}>Download file</a>
              </Button>
            )}
            <Button
              variant="primary"
              loading={validating}
              onClick={async () => {
                try {
                  await validateFile({
                    variables: { input: { importFileId: file.id } },
                  });
                  toast({
                    title: "Revalidated",
                    kind: "success",
                    autoHide: true,
                  });
                } catch (error) {
                  toast({
                    title: "Error revalidating file",
                    description: String(error),
                    kind: "error",
                    autoHide: false,
                  });
                }
              }}
            >
              Revalidate
            </Button>
          </div>
        }
      />

      <DataTable
        data={validations}
        columns={columns}
        hidePagination={true}
        pageSize={10_000}
        className="overflow-hidden border"
        linkTo={(row) => `/assets/${row.original.asset.id}`}
        toolbarComponent={ValidationsTableToolbar}
      />
    </AdminPageContainer>
  );
}

function ValidationsTableToolbar({
  table,
}: DataTableToolbarProps<AdminAssetBalanceValidationRowFragment>) {
  const organisationNames = Array.from(
    table.getColumn("entity")?.getFacetedUniqueValues().keys() ?? [],
  ).sort();
  const fundNames = Array.from(
    table.getColumn("account")?.getFacetedUniqueValues().keys() ?? [],
  ).sort();

  return (
    <div className="flex flex-row flex-wrap items-center gap-2">
      <FacetedFilterStandalone
        column={table.getColumn("account")}
        title="Fund"
        options={fundNames.map((name) => ({ value: name, label: name }))}
      />
      <FacetedFilterStandalone
        column={table.getColumn("status")}
        title="Status"
        options={assetBalanceValidationStatusValues.map((value) => ({
          value,
          label: value === "MATCHED" ? "Matched" : "Mismatched",
        }))}
      />
      <FacetedFilterStandalone
        column={table.getColumn("nonAssetUnits")}
        title="Non-asset units"
        options={[
          { value: "YES", label: "Yes" },
          { value: "NO", label: "No" },
        ]}
      />
      <FacetedFilterStandalone
        column={table.getColumn("entity")}
        title="Organisation"
        options={organisationNames.map((name) => ({
          value: name,
          label: name,
        }))}
      />
      <ResetButton table={table} />
    </div>
  );
}

const columns: ColumnDef<AdminAssetBalanceValidationRowFragment>[] = [
  {
    id: "entity",
    accessorFn: (row) => row.asset.businessEntity?.organisation?.name ?? "-",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Organisation" />
    ),
    filterFn: (row, id, value: string[]) => value.includes(row.getValue(id)),
    cell: ({ row }) => (
      <BusinessEntityColumnCell
        businessEntity={row.original.asset.businessEntity}
      />
    ),
  },
  {
    id: "account",
    accessorFn: (row) =>
      row.asset.__typename === "FixedIncomeFundAsset" && row.asset.product
        ? row.asset.product.name
        : row.asset.name,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Account" />
    ),
    filterFn: (row, id, value: string[]) => value.includes(row.getValue(id)),
    cell: ({ row }) => (
      <Link to="/assets/$assetId" params={{ assetId: row.original.asset.id }}>
        {row.original.asset.__typename === "FixedIncomeFundAsset"
          ? row.original.asset.product?.name
          : row.original.asset.name}{" "}
        ({row.original.asset.friendlyIdentifier ?? "-"})
      </Link>
    ),
  },
  {
    id: "effectiveAt",
    accessorFn: (row) => row.effectiveAt,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Effective at"
        align="left"
      />
    ),
    cell: ({ row }) => {
      return <DateUI date={row.original.effectiveAt} variant="DATETIME" />;
    },
  },
  {
    id: "ledgerUnitBalance",
    accessorFn: (row) => row.ledgerUnitBalance.toNumber(),
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Ledger units"
        align="right"
      />
    ),
    cell: ({ row }) => (
      <div className="text-right">
        <NumberUI
          value={row.original.ledgerUnitBalance}
          format={NumberUI.format.decimal({ digits: 4 })}
        />
      </div>
    ),
  },
  {
    id: "fileUnitBalance",
    accessorFn: (row) => row.fileUnitBalance.toNumber(),
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="File units" align="right" />
    ),
    cell: ({ row }) => (
      <div className="text-right">
        <NumberUI
          value={row.original.fileUnitBalance}
          format={NumberUI.format.decimal({ digits: 4 })}
        />
      </div>
    ),
  },
  {
    id: "unitBalanceDifference",
    accessorFn: (row) => row.unitBalanceDifference.toNumber(),
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Unit difference"
        align="right"
      />
    ),
    cell: ({ row }) => (
      <div className="text-right">
        <NumberUI
          value={row.original.unitBalanceDifference}
          format={NumberUI.format.decimal({ digits: 4 })}
        />
      </div>
    ),
  },
  {
    id: "nonAssetUnits",
    accessorFn: (row) => (row.nonAssetUnits ? "YES" : "NO"),
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Non-asset units" />
    ),
    filterFn: (row, id, value: string[]) => value.includes(row.getValue(id)),
    cell: ({ row }) =>
      row.original.nonAssetUnits ? (
        <LabelPill variant="warning" label="Yes" />
      ) : (
        "-"
      ),
  },
  {
    id: "status",
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    filterFn: (row, id, value: string[]) => value.includes(row.getValue(id)),
    cell: ({ row }) => (
      <LabelPill
        variant={row.original.status === "MATCHED" ? "success" : "error"}
        label={row.original.status === "MATCHED" ? "Matched" : "Mismatched"}
      />
    ),
  },
];
